import React, { createContext, useContext, useState, useEffect } from 'react';
import { AppContext } from '../../context/Context';
import { useAuthStore } from '../index';

/**
 * Migration Provider - Gradual transition from Context API to Zustand stores
 * 
 * This provider acts as a bridge during the migration process:
 * 1. Keeps Context API as fallback for safety
 * 2. Gradually migrates state to Zustand stores
 * 3. Provides feature flags to control migration progress
 * 4. Ensures backward compatibility during transition
 */

interface MigrationFlags {
  useAuthStore: boolean;
  useRestrictionsStore: boolean;
  useNavigationStore: boolean;
  useTableStore: boolean;
  useInfoSidebarStore: boolean;
}

interface MigrationContextType {
  flags: MigrationFlags;
  setFlag: (flag: keyof MigrationFlags, value: boolean) => void;
  isFullyMigrated: boolean;
}

const MigrationContext = createContext<MigrationContextType | null>(null);

export const useMigration = () => {
  const context = useContext(MigrationContext);
  if (!context) {
    throw new Error('useMigration must be used within MigrationProvider');
  }
  return context;
};

interface MigrationProviderProps {
  children: React.ReactNode;
  initialFlags?: Partial<MigrationFlags>;
}

export const MigrationProvider: React.FC<MigrationProviderProps> = ({ 
  children, 
  initialFlags = {} 
}) => {
  const [flags, setFlags] = useState<MigrationFlags>({
    useAuthStore: true, // Start with auth migration (least risky)
    useRestrictionsStore: false,
    useNavigationStore: false,
    useTableStore: false,
    useInfoSidebarStore: false,
    ...initialFlags,
  });

  const setFlag = (flag: keyof MigrationFlags, value: boolean) => {
    setFlags(prev => ({ ...prev, [flag]: value }));
  };

  const isFullyMigrated = Object.values(flags).every(flag => flag);

  const contextValue: MigrationContextType = {
    flags,
    setFlag,
    isFullyMigrated,
  };

  return (
    <MigrationContext.Provider value={contextValue}>
      {children}
    </MigrationContext.Provider>
  );
};

/**
 * Auth Migration Hook - Bridges Context API and AuthStore
 */
export const useAuthMigration = () => {
  const { flags } = useMigration();
  const contextAuth = useContext(AppContext);
  const authStore = useAuthStore();

  if (flags.useAuthStore) {
    // Use Zustand store
    return {
      isAuthenticated: authStore.isAuthenticated,
      setIsAuthenticated: (value: boolean) => {
        if (value) {
          // This would typically be handled by login action
          authStore.setUser(contextAuth?.user || null);
          authStore.setAuthorization(contextAuth?.authorization || '');
          authStore.setGroups(contextAuth?.groups || []);
        } else {
          authStore.logout();
        }
      },
      authorization: authStore.authorization,
      setAuthorization: authStore.setAuthorization,
      groups: authStore.groups,
      setGroups: authStore.setGroups,
      user: authStore.user,
      setUser: authStore.setUser,
      userApiKey: authStore.userApiKey,
      setUserApiKey: authStore.setUserApiKey,
      authError: authStore.authError,
      setAuthError: authStore.setAuthError,
      userType: authStore.userType,
      setUserType: authStore.setUserType,
      // Store-specific methods
      login: authStore.login,
      logout: authStore.logout,
      initializeAuth: authStore.initializeAuth,
      fetchUserType: authStore.fetchUserType,
    };
  } else {
    // Use Context API (fallback)
    return {
      isAuthenticated: contextAuth?.isAuthenticated || false,
      setIsAuthenticated: contextAuth?.setIsAuthenticated || (() => {}),
      authorization: contextAuth?.authorization || '',
      setAuthorization: contextAuth?.setAuthorization || (() => {}),
      groups: contextAuth?.groups || [],
      setGroups: contextAuth?.setGroups || (() => {}),
      user: contextAuth?.user || null,
      setUser: contextAuth?.setUser || (() => {}),
      userApiKey: contextAuth?.userApiKey || [],
      setUserApiKey: contextAuth?.setUserApiKey || (() => {}),
      authError: contextAuth?.authError || '',
      setAuthError: contextAuth?.setAuthError || (() => {}),
      userType: null, // Not available in context
      setUserType: () => {}, // Not available in context
      // Placeholder methods for store-specific functionality
      login: async () => {},
      logout: async () => {},
      initializeAuth: async () => {},
      fetchUserType: async () => {},
    };
  }
};

/**
 * Development utilities for migration testing
 */
export const MigrationDevTools = () => {
  const { flags, setFlag, isFullyMigrated } = useMigration();

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 10,
        right: 10,
        background: 'white',
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 10000,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
    >
      <h4>Migration Status</h4>
      <div>
        <strong>Fully Migrated:</strong> {isFullyMigrated ? '✅' : '❌'}
      </div>
      {Object.entries(flags).map(([key, value]) => (
        <div key={key} style={{ margin: '5px 0' }}>
          <label>
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => setFlag(key as keyof MigrationFlags, e.target.checked)}
            />
            {key}: {value ? '✅' : '❌'}
          </label>
        </div>
      ))}
    </div>
  );
};